/** Sign-in screens for the hosted console (decision 0025), in front of
 * milestone 2's login (decision 0019): email and password, then the
 * authenticator code -- or, on an invitee's first sign-in, a new
 * password and authenticator enrolment first -- then the terms, then
 * the console. Nothing here decides access: every step is the server's
 * answer, and the console itself is only fetched once the server says a
 * session exists and the terms are accepted. */
import {
  ApiError,
  acceptTerms,
  isSessionStarted,
  signIn,
  submitMfaCode,
  submitMfaSetup,
  submitNewPassword,
} from "./api.js";
import type { SignInResult } from "./api.js";
import { NEW_USER_COPY, REACCEPT_COPY, termsVariant } from "./terms-copy.js";
import type { TermsRequired } from "./terms-copy.js";

type Done = () => void;

// Published from the same committed text this step shows and hashes
// (docs/legal/Preekie_Privacy_Policy_v1.1.md, server-side sibling of
// the Software Terms) -- see site/privacy.html.
const PRIVACY_POLICY_URL = "https://preekie.com/privacy.html";
const REQUEST_ACCESS_URL = "https://preekie.com/request-access.html";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Preekie couldn't be reached. Check your connection and try again.";
}

/** The card every step shares: wordmark (sign-in is the one product
 * surface it appears on -- PRODUCT-UI.md), heading, optional lead text,
 * a form, a live error region and the legal footer. */
function card(root: HTMLElement, heading: string, lead?: string) {
  root.replaceChildren();
  const wrap = el("div", "pk-auth-card");
  const logo = el("img", "pk-auth-wordmark");
  logo.src = "/brand/preekie-wordmark-ink.svg";
  logo.alt = "Preekie";
  logo.width = 112; // the SVG's own 3207:919 aspect at 32px high
  logo.height = 32;
  const h = el("h1", "pk-auth-heading", heading);
  h.tabIndex = -1;
  wrap.append(logo, h);
  if (lead) wrap.append(el("p", "pk-auth-lead", lead));
  const form = el("form", "pk-auth-form");
  form.noValidate = true;
  const error = el("p", "pk-auth-error");
  error.setAttribute("role", "alert");
  error.hidden = true;
  wrap.append(form, error);
  root.append(wrap, el("p", "pk-auth-footer", "Preekie is a product of CPAI Australia Pty Ltd · ACN 674 037 668"));
  h.focus();
  return { wrap, form, error };
}

function field(form: HTMLFormElement, label: string, input: Partial<HTMLInputElement>): HTMLInputElement {
  const id = `pk-${input.name}`;
  const lab = el("label", "pk-auth-label", label);
  lab.htmlFor = id;
  const box = el("input", "pk-auth-input");
  Object.assign(box, input, { id, required: true });
  form.append(lab, box);
  return box;
}

function privacyLink(text: string): HTMLAnchorElement {
  const a = el("a", "pk-auth-link", text);
  a.href = PRIVACY_POLICY_URL;
  a.target = "_blank";
  a.rel = "noopener";
  return a;
}

function submitButton(form: HTMLFormElement, label: string): HTMLButtonElement {
  const button = el("button", "btn btn-primary pk-auth-submit", label);
  button.type = "submit";
  form.append(button);
  return button;
}

/** Wires a form's submit to `action`, with the button disabled while it
 * runs and any failure shown in the card's error region. */
function onSubmit(
  parts: { form: HTMLFormElement; error: HTMLElement },
  button: HTMLButtonElement,
  action: () => Promise<void>
): void {
  parts.form.addEventListener("submit", (event) => {
    event.preventDefault();
    parts.error.hidden = true;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = "Checking…";
    action()
      .catch((err: unknown) => {
        parts.error.textContent = messageFor(err);
        parts.error.hidden = false;
      })
      .finally(() => {
        button.disabled = false;
        button.textContent = label;
      });
  });
}

export function showSignIn(root: HTMLElement, done: Done, notice?: string, superAdmin = false): void {
  // CAP-009: the super admin's separate sign-in (its own pool and session) uses these same steps; useLoginBase() picks
  // the endpoints. It is never combined with a customer account.
  const parts = card(root, superAdmin ? "Super admin sign-in" : "Sign in to Preekie", notice);
  const email = field(parts.form, "Email", { name: "email", type: "email", autocomplete: "username" });
  const password = field(parts.form, "Password", {
    name: "password",
    type: "password",
    autocomplete: "current-password",
  });
  const button = submitButton(parts.form, "Sign in");
  onSubmit(parts, button, async () => {
    const result = await signIn(email.value.trim(), password.value);
    next(root, email.value.trim(), result, done);
  });
  if (superAdmin) {
    parts.wrap.append(el("p", "pk-auth-note", "For Preekie's platform administration only. Every action is logged."));
    email.focus();
    return;
  }
  // The website's Sign in lands here; accounts are created by invitation.
  const invite = el("p", "pk-auth-note", "Access is by invitation. ");
  const request = el("a", "pk-auth-link", "Request access");
  request.href = REQUEST_ACCESS_URL;
  invite.append(request);
  parts.wrap.append(invite, privacyLink("Privacy Policy"));
  email.focus();
}

function next(root: HTMLElement, email: string, result: SignInResult, done: Done): void {
  if (isSessionStarted(result)) {
    // Signed in. Whether the terms still need accepting is answered by
    // the console's own first request (a 403 naming the exact version
    // and hash -- see main.ts), not assumed here.
    done();
    return;
  }
  if (result.challenge === "SOFTWARE_TOKEN_MFA") showMfaCode(root, email, result.session, done);
  else if (result.challenge === "NEW_PASSWORD_REQUIRED") showNewPassword(root, email, result.session, done);
  else showMfaSetup(root, email, result, done);
}

function codeField(form: HTMLFormElement): HTMLInputElement {
  return field(form, "6-digit code", {
    name: "code",
    type: "text",
    inputMode: "numeric",
    autocomplete: "one-time-code",
    pattern: "[0-9]{6}",
    maxLength: 6,
  });
}

function showMfaCode(root: HTMLElement, email: string, session: string, done: Done): void {
  const parts = card(root, "Enter your code", "Open your authenticator app and enter the code for Preekie.");
  const code = codeField(parts.form);
  const button = submitButton(parts.form, "Continue");
  onSubmit(parts, button, async () => {
    next(root, email, await submitMfaCode(email, session, code.value.trim()), done);
  });
  code.focus();
}

function showNewPassword(root: HTMLElement, email: string, session: string, done: Done): void {
  const parts = card(
    root,
    "Choose your password",
    "Replace the temporary password you were given. Use at least 12 characters, with upper and lower case letters, a number and a symbol."
  );
  const password = field(parts.form, "New password", {
    name: "new_password",
    type: "password",
    autocomplete: "new-password",
  });
  const confirm = field(parts.form, "Confirm new password", {
    name: "confirm_password",
    type: "password",
    autocomplete: "new-password",
  });
  const button = submitButton(parts.form, "Save password");
  onSubmit(parts, button, async () => {
    if (password.value !== confirm.value) {
      throw new ApiError("The two passwords don't match. Type them again.", 400, null);
    }
    next(root, email, await submitNewPassword(email, session, password.value), done);
  });
  password.focus();
}

type MfaSetup = Extract<SignInResult, { challenge: "MFA_SETUP" }>;

/** The QR code, from the SVG our own server generated (qr.py). Parsed as
 * SVG and imported as DOM nodes -- never innerHTML -- and refused unless
 * it is a plain <svg> with nothing scriptable in it. The CSP needs no
 * change for this: no inline script or style is involved. */
function qrFigure(svg: string): HTMLElement | null {
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = doc.documentElement;
  if (root.nodeName !== "svg" || doc.querySelector("parsererror, script, foreignObject")) return null;
  root.setAttribute("aria-hidden", "true");
  const figure = el("figure", "pk-auth-qr");
  figure.setAttribute("role", "img");
  figure.setAttribute("aria-label", "QR code for adding Preekie to your authenticator app");
  figure.append(document.importNode(root, true));
  return figure;
}

function isTouchDevice(): boolean {
  return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

/** First sign-in -- and re-enrolment after an MFA reset, which is the
 * same MFA_SETUP challenge. The QR code, then the key for anyone who
 * can't scan, then the code that proves the app is set up. The QR and
 * the key are removed from the page as soon as enrolment succeeds. */
function showMfaSetup(root: HTMLElement, email: string, setup: MfaSetup, done: Done): void {
  const parts = card(
    root,
    "Add Preekie to your authenticator app",
    "Preekie asks for a code from an authenticator app each time you sign in."
  );
  const secretNodes: HTMLElement[] = [];

  const scan = el("p", "pk-auth-lead", "Scan this with your authenticator app, then enter the 6-digit code it shows.");
  const apps = el("p", "pk-auth-note", "Use an app such as Microsoft Authenticator or Google Authenticator.");
  const figure = qrFigure(setup.qr_svg);
  if (figure) secretNodes.push(figure);

  // On a phone, the QR is on the same screen as the app -- offer the link.
  const openLink = el("a", "btn btn-outline pk-auth-open-app", "Open in authenticator app");
  openLink.href = setup.otpauth_uri;
  openLink.hidden = !isTouchDevice();
  secretNodes.push(openLink);

  // "Can't scan it?": the key in groups of 4, with a Copy button.
  const manual = el("details", "pk-auth-manual");
  manual.append(el("summary", "pk-auth-manual-summary", "Can't scan it?"));
  manual.append(el("p", "pk-auth-note", "In your app, add an account and enter this setup key:"));
  const key = el("p", "pk-auth-secret figure", setup.secret_code.replace(/(.{4})/g, "$1 ").trim());
  key.setAttribute("aria-label", `Setup key: ${setup.secret_code.split("").join(" ")}`);
  const copy = el("button", "btn btn-outline pk-auth-copy", "Copy key");
  copy.type = "button";
  const copied = el("span", "pk-auth-copied");
  copied.setAttribute("role", "status");
  copy.addEventListener("click", () => {
    navigator.clipboard.writeText(setup.secret_code).then(
      () => (copied.textContent = "Setup key copied."),
      () => (copied.textContent = "Couldn't copy it. Select the key and copy it instead.")
    );
  });
  manual.append(key, copy, copied);
  secretNodes.push(manual);

  parts.form.before(scan, apps, ...(figure ? [figure] : []), openLink, manual);
  const code = codeField(parts.form);
  const button = submitButton(parts.form, "Verify and continue");
  onSubmit(parts, button, async () => {
    const result = await submitMfaSetup(email, setup.session, code.value.trim());
    // Enrolled: take the QR, the link and the key off the page before
    // anything else renders.
    for (const node of secretNodes) node.remove();
    next(root, email, result, done);
  });
  code.focus();
}

/** CAP-009: an invitee's authenticator set-up after accepting an invite (the same MFA_SETUP challenge and screen). */
export function showAuthenticatorSetup(root: HTMLElement, email: string, setup: MfaSetup, done: () => void): void {
  showMfaSetup(root, email, setup, done);
}

/** Splits on **bold** spans and appends safe text/strong nodes -- never
 * innerHTML. The source is our own committed document (never anything
 * the server echoes back from a caller), but the same discipline
 * qrFigure already uses for the QR SVG applies here too: build the DOM
 * directly, don't parse HTML. */
function inlineText(parent: HTMLElement, line: string): void {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  for (const part of parts) {
    if (part.startsWith("**") && part.endsWith("**")) {
      const strong = el("strong", undefined, part.slice(2, -2));
      parent.append(strong);
    } else if (part) {
      parent.append(document.createTextNode(part));
    }
  }
}

/** A small, purpose-built renderer for exactly the markdown subset
 * docs/legal/*.md uses (## headings, "- " lists, **bold** spans, plain
 * paragraphs) -- not a general markdown library, the same reasoning
 * this file already applies elsewhere (a narrow tool built for what's
 * actually needed, e.g. the hand-written TOTP-adjacent QR handling).
 * The leading "# Title" line is skipped: the card's own heading already
 * carries it. */
function legalDocument(text: string): HTMLElement {
  const box = el("div", "pk-auth-terms-text");
  box.setAttribute("tabindex", "0");
  let list: HTMLUListElement | null = null;
  for (const raw of text.split("\n")) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      list = null;
      continue;
    }
    if (line.startsWith("# ")) continue;
    if (line.startsWith("## ")) {
      list = null;
      const h = el("h2");
      inlineText(h, line.slice(3));
      box.append(h);
      continue;
    }
    if (line.startsWith("- ")) {
      if (!list) {
        list = el("ul");
        box.append(list);
      }
      const li = el("li");
      inlineText(li, line.slice(2));
      list.append(li);
      continue;
    }
    list = null;
    const p = el("p");
    inlineText(p, line);
    box.append(p);
  }
  return box;
}

/** The terms step, for exactly the version, hash and text the server's
 * `terms_required` response named -- the same three facts recorded
 * against this acceptance (services/tenancy/terms.py), so what's shown
 * here is provably what gets accepted, not a paraphrase of it. */
export function showTerms(
  root: HTMLElement,
  version: string,
  hash: string,
  text: string,
  done: Done,
  opts: { body?: TermsRequired; onSignOut?: () => Promise<unknown> } = {}
): void {
  // Privacy Policy v1.2 (C9, RJ Legal 25 Oct): the approved wording, word for word, for exactly the versions it names.
  const variant = termsVariant(opts.body ?? {});
  if (variant === "update") return showPolicyUpdate(root, version, hash, text, done, opts.onSignOut);
  if (variant === "new") {
    const parts = card(root, NEW_USER_COPY.heading, NEW_USER_COPY.lead);
    parts.form.before(el("p", "pk-auth-lead pk-auth-versions", NEW_USER_COPY.versions), legalDocument(text), privacyLink(NEW_USER_COPY.readPrivacy));
    const button = submitButton(parts.form, NEW_USER_COPY.accept);
    onSubmit(parts, button, async () => {
      await acceptTerms(version, hash);
      done();
    });
    return;
  }
  const parts = card(
    root,
    "Accept the Software Terms",
    "Read the terms below, then accept to continue. Accepting also confirms you've had the chance to read our Privacy Policy."
  );
  parts.form.before(
    el("p", "pk-auth-lead", `Version ${version}`),
    el("p", "pk-auth-note pk-auth-hash", `Document hash: ${hash}`),
    legalDocument(text),
    privacyLink("Read the Privacy Policy")
  );
  const button = submitButton(parts.form, "Accept and continue");
  onSubmit(parts, button, async () => {
    await acceptTerms(version, hash);
    done();
  });
}

/** C9 (existing users): what's new in v1.2, both documents to read (the Terms in place: there's no public page), then Accept and
 * continue or Sign out -- nothing else until they accept. The version and hash accepted are exactly the server's. */
function showPolicyUpdate(root: HTMLElement, version: string, hash: string, text: string, done: Done, onSignOut?: () => Promise<unknown>): void {
  const parts = card(root, REACCEPT_COPY.heading, REACCEPT_COPY.lead);
  const list = el("ul", "pk-auth-news");
  for (const b of REACCEPT_COPY.bullets) list.append(el("li", undefined, b));
  const terms = el("details", "pk-auth-manual pk-auth-terms-more");
  terms.append(el("summary", "pk-auth-manual-summary", REACCEPT_COPY.readTerms), legalDocument(text));
  const confirm = el("p", "pk-auth-note pk-auth-confirm", REACCEPT_COPY.confirmBefore);
  confirm.append(el("strong", undefined, REACCEPT_COPY.accept), REACCEPT_COPY.confirmAfter);
  parts.form.before(el("h2", "pk-auth-subheading", REACCEPT_COPY.whatsNew), list, privacyLink(REACCEPT_COPY.readPrivacy), terms, confirm);
  const button = submitButton(parts.form, REACCEPT_COPY.accept);
  if (onSignOut) {
    const out = el("button", "btn btn-outline pk-auth-signout", REACCEPT_COPY.signOut);
    out.type = "button";
    // Only a sign-out the server confirmed leaves this step (E's finding 6): a failure is shown here and the button stays.
    out.addEventListener("click", () => {
      parts.error.hidden = true;
      out.disabled = true;
      onSignOut()
        .catch((err: unknown) => {
          parts.error.textContent = messageFor(err);
          parts.error.hidden = false;
        })
        .finally(() => {
          out.disabled = false;
        });
    });
    parts.form.append(out);
  }
  onSubmit(parts, button, async () => {
    await acceptTerms(version, hash);
    done();
  });
}
